import React from 'react';

import Button from '@hero/ui-kit/inputs/Button';
import { useUserStatusContext } from '../../../../../../context/UserStatus';
import useCreateDrChronoTask from '../../api/useCreateDrChronoTask';
import useGetDrChronoTasks from '../../api/useGetDrChronoTasks';
import useGetDrChronoTaskStatuses from '../../api/useGetDrChronoTaskStatuses';
import useUpdateDrChronoTask from '../../api/useUpdateDrChronoTask';
import { DrChronoTask } from '../../types';
import {
    getDefaultStatusId,
    getPriorityLabel,
    getTaskErrorMessage,
    getTaskStatus,
    TaskFormValues,
    toTaskPayload,
    toTaskUpdate
} from '../../utils/drChronoTasks';
import { formatDate } from '../../utils/formatters';
import * as CardStyle from '../../style.module.scss';
import EhrCard from '../EhrCard';
import StatusPill from '../StatusPill';
import TaskForm from './TaskForm';
import * as Style from './style.module.scss';

type Props = { memberId: number | null | undefined };

/**
 * The open form; `task` is set when editing. It is a snapshot from when Edit was clicked, on
 * purpose: the update diffs against what the user saw, not against a refetched copy.
 */
type Editing = { task?: DrChronoTask } | null;

const Tasks: React.FC<Props> = ({ memberId }) => {
    const { permissions } = useUserStatusContext();
    const [editing, setEditing] = React.useState<Editing>(null);

    const { data: tasks = [], isLoading } = useGetDrChronoTasks(memberId);
    const { data: statuses = [] } = useGetDrChronoTaskStatuses();
    const create = useCreateDrChronoTask(memberId, () => setEditing(null));
    const update = useUpdateDrChronoTask(memberId, () => setEditing(null));

    // Statuses come from the org's DrChrono task setup; without one tasks can't be written.
    const defaultStatusId = getDefaultStatusId(statuses);
    const canEdit = permissions.leads.leadListEdit && defaultStatusId !== undefined;
    const editingTask = editing?.task;
    const mutation = editingTask ? update : create;

    const open = (next: Editing) => {
        create.reset();
        update.reset();
        setEditing(next);
    };

    const submit = (values: TaskFormValues) => {
        if (editingTask) {
            update.mutate({ taskId: editingTask.id, body: toTaskUpdate(values, editingTask) });
        } else if (defaultStatusId !== undefined) {
            create.mutate(toTaskPayload(values, defaultStatusId));
        }
    };

    return (
        <EhrCard
            title="Tasks"
            isLoading={isLoading}
            isEmpty={!tasks.length}
            emptyMessage="No DrChrono tasks for this member."
            action={
                canEdit && !editing ? (
                    <Button size="small" onClick={() => open({})}>
                        + New Task
                    </Button>
                ) : null
            }
            banner={
                canEdit && editing ? (
                    <TaskForm
                        // Remount per task so the form takes the next task's values.
                        key={editingTask?.id ?? 'new'}
                        task={editingTask}
                        statuses={statuses}
                        isPending={mutation.isPending}
                        errorMessage={getTaskErrorMessage(mutation.error)}
                        onSubmit={submit}
                        onCancel={() => setEditing(null)}
                    />
                ) : null
            }
        >
            <div className={`${CardStyle.list} ${Style.list}`}>
                {tasks.map((task) => {
                    const status = getTaskStatus(task, statuses);
                    const priority = getPriorityLabel(task.priority);
                    const details = [
                        task.due_at ? `Due ${formatDate(task.due_at)}` : null,
                        priority ? `${priority} priority` : null
                    ]
                        .filter(Boolean)
                        .join(' · ');
                    const isBeingEdited = editingTask?.id === task.id;
                    // Closed tasks are done; they stay in the list but can't be edited.
                    const isEditable = canEdit && !status.isClosed;

                    return (
                        <div key={task.id} className={CardStyle.listRow}>
                            <StatusPill
                                label={status.label}
                                tone={status.isClosed ? 'neutral' : 'warning'}
                            />
                            <div className={Style.title}>
                                <span className={task.title ? undefined : CardStyle.muted}>
                                    {task.title || 'Untitled task'}
                                </span>
                                {details || isEditable ? (
                                    <span className={CardStyle.subText}>
                                        {details}
                                        {details && isEditable ? ' · ' : null}
                                        {isEditable ? (
                                            <button
                                                type="button"
                                                className={CardStyle.linkButton}
                                                disabled={isBeingEdited}
                                                onClick={() => open({ task })}
                                            >
                                                {isBeingEdited ? 'Editing' : 'Edit'}
                                            </button>
                                        ) : null}
                                    </span>
                                ) : null}
                            </div>
                            <span className={CardStyle.code}>
                                {formatDate(task.drchrono_created_at)}
                            </span>
                        </div>
                    );
                })}
            </div>
        </EhrCard>
    );
};

export default Tasks;
